import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import {
  claimNativeInputSyncDispatch,
  useNativeInputSync,
} from "../hooks/use-native-input-sync"
import { Textarea } from "../components/ui/textarea"
import { stripPresetConflicts } from "../lib/preset-utils"

export function TextareaRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("textarea")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const placeholder = element.placeholder ? String(resolveSymbol(element.placeholder) ?? "") : undefined
  const ariaLabel = element.ariaLabel
    ? String(resolveSymbol(element.ariaLabel) ?? element.ariaLabel)
    : undefined
  const textDefault = element.text ? String(resolveSymbol(element.text) ?? "") : ""
  const elementRecord = element as unknown as Record<string, unknown>
  const requiredValue = elementRecord.required
  const required = requiredValue === true || requiredValue === "true"
  const disabledValue = typeof elementRecord.disabled === "string" && elementRecord.disabled.includes("#{")
    ? resolveSymbol(elementRecord.disabled)
    : elementRecord.disabled
  const disabled = disabledValue === true || disabledValue === "true" || disabledValue === 1
  const rowsValue = typeof elementRecord.rows === "number"
    ? elementRecord.rows
    : Number(elementRecord.rows)
  const rows = Number.isInteger(rowsValue) && rowsValue > 0 ? rowsValue : undefined

  const [localValue, setLocalValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    textDefault,
    String,
  )

  // Native DOM input event listener — catches Playwright page.fill()/page.type()
  const nativeRef = useNativeInputSync((value) => {
    setLocalValue(value)
    // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
    dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
  })

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value)
    if (claimNativeInputSyncDispatch(e.nativeEvent)) {
      dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value: e.target.value })
    }
  }

  return (
    <Textarea
      ref={nativeRef}
      id={element.id}
      name={element.name}
      aria-label={ariaLabel}
      className={stripPresetConflicts(element.className, undefined, element.designAuthorityClassName)}
      placeholder={placeholder}
      required={required || undefined}
      disabled={disabled || undefined}
      rows={rows}
      {...(element.bind !== undefined ? { value: localValue } : { defaultValue: textDefault })}
      onChange={handleChange}
      data-testid={`textarea-${element.id}`}
      data-action="input"
      data-atom-type="textarea"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    />
  )
}
